import { motion } from 'motion/react';
import { ArrowUpRight } from 'lucide-react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faSackDollar,
  faFilter,
  faListCheck,
  faCalendarDays,
  faRss,
  faFire,
  faTableCells,
  faBookOpen,
  faChartColumn,
  faCamera,
  faMicrophone,
  faPeopleRoof,
  faNewspaper,
  faBuildingColumns,
  faChartPie,
  faChartLine,
  faSave,
  faArrowTrendUp,
} from '@fortawesome/free-solid-svg-icons';
import LinkButton from "~/ui-apps/design-system/atoms/LinkButton";

type FeatureOffering = {
  label: string;
  icon: IconDefinition;
};

const featurePillars: { title: string; features: FeatureOffering[] }[] = [
  {
    title: 'Discover',
    features: [
      { label: 'Guided Text Screener', icon: faFilter },
      { label: 'Evaluation Criteria', icon: faListCheck },
      { label: 'Earnings Calendar', icon: faCalendarDays },
      { label: 'Personalized Feed', icon: faRss },
      { label: 'Socially Trending Tickers', icon: faFire },
    ],
  },
  {
    title: 'Evaluate',
    features: [
      { label: 'Ticker Breakdowns', icon: faTableCells },
      { label: 'Research Digests', icon: faBookOpen },
      { label: 'Visualized Financials', icon: faChartColumn },
      { label: 'Earnings Snapshots', icon: faCamera },
      { label: 'Earnings Transcripts', icon: faMicrophone },
      { label: 'Insider Trades', icon: faPeopleRoof },
      { label: 'News Center', icon: faNewspaper },
      { label: 'Institutional Holdings', icon: faBuildingColumns },
      { label: 'Shares Stats', icon: faChartPie },
    ],
  },
  {
    title: 'Forecast',
    features: [
      { label: 'Project Price Returns', icon: faChartLine },
      { label: 'Save Forecast', icon: faSave },
      { label: 'Track Progress', icon: faArrowTrendUp },
    ],
  },
];

const PricingSection: React.FC = () => {
  return (
    <section
      className="relative overflow-hidden py-16 px-1.5 lg:py-24"
      style={{
        background:
          "linear-gradient(165deg, #f4f1ea 0%, #fcfcf9 38%, #f8f6f1 100%)",
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 10%, rgba(0,0,0,0.06) 0%, transparent 45%),
            radial-gradient(circle at 85% 60%, rgba(0,0,0,0.05) 0%, transparent 40%)`,
        }}
        aria-hidden
      />
      <div className="max-w-[1200px] mx-auto relative">
        <div className="mx-auto flex w-full flex-col items-center gap-8 md:gap-10">
          <motion.div
            id="pricing"
            className="w-full max-w-lg scroll-mt-20 text-center md:max-w-2xl lg:scroll-mt-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-4 flex justify-center md:mt-8">
              <span
                className="shrink-0 p-4 py-5 border bg-yellow-50 border-gray-300 rounded-3xl md:rounded-[2rem] shadow-md transition-shadow duration-300 inline-flex"
                aria-hidden
              >
                <FontAwesomeIcon icon={faSackDollar} className="text-yellow-600 text-3xl md:text-4xl" />
              </span>
            </div>
            <h2 className="tracking-tight font-black text-[29px] md:text-5xl leading-snug md:leading-tight">
              No credit card required.
              <span className="mt-2 block text-[17px] font-semibold leading-snug text-yellow-600/75 md:mt-4 md:text-2xl md:font-bold md:leading-snug">
                We only deserve to be paid if the trial is useful.
              </span>
            </h2>
          </motion.div>

          <motion.div
            className="w-full max-w-lg md:max-w-3xl"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.65, delay: 0.1 }}
          >
            <div
              className="rounded-[1.75rem] p-px relative"
              style={{
                background:
                  "linear-gradient(145deg, rgba(0,0,0,0.12), rgba(0,0,0,0.04) 40%, rgba(0,0,0,0.08))",
                boxShadow:
                  "0 24px 48px -24px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
              }}
            >
              <div
                className="rounded-[1.7rem] overflow-hidden relative"
                style={{
                  background: "#fff",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
                }}
              >
                <div
                  className="h-1 w-full"
                  style={{
                    background:
                      "linear-gradient(90deg, #171717 0%, #404040 45%, #171717 100%)",
                  }}
                  aria-hidden
                />

                <div
                  className="absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full text-[11px] tracking-wide ring-1 ring-amber-900/10"
                  style={{
                    background: "#edd78f",
                    color: "#3a3018",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.65), 0 2px 8px rgba(200, 165, 80, 0.22)",
                  }}
                >
                  30-DAY FREE TRIAL
                </div>

                <div className="p-4 md:p-8 pt-7">
                  <h3 className="mb-8 pr-[10.5rem] sm:pr-44 text-xl font-extrabold tracking-tight text-black/90">
                    Premium
                  </h3>

                  <div className="mb-5 md:mb-8">
                    <div className="flex flex-wrap items-baseline justify-center gap-1.5 text-center">
                      <span
                        className="tabular-nums"
                        style={{
                          fontSize: "clamp(3rem, 8vw, 3.75rem)",
                          fontWeight: 700,
                          lineHeight: 1,
                          letterSpacing: "-0.03em",
                        }}
                      >
                        $5
                      </span>
                      <span className="text-black/45 text-base font-medium pb-1">/month</span>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <LinkButton
                      type="primary"
                      size="medium"
                      href="/sign-up"
                      icon={
                        <ArrowUpRight
                          className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          strokeWidth={2}
                          aria-hidden
                        />
                      }
                      iconPosition="right"
                      className="group mb-4 flex h-10 w-auto min-w-[14rem] items-center justify-center !rounded-full px-8 text-sm hover:!bg-black/90 md:mb-5"
                    >
                      Start for free
                    </LinkButton>
                  </div>
                  <p className="mb-8 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-[13px] font-medium leading-relaxed text-yellow-600">
                    <span>Monthly</span>
                    <span aria-hidden className="text-black/40">•</span>
                    <span>No yearly lock-in tricks</span>
                    <span aria-hidden className="text-black/40">•</span>
                    <span>Cancel anytime</span>
                  </p>

                  <ol className="relative space-y-0">
                    {featurePillars.map((pillar, pillarIndex) => {
                      const isLast = pillarIndex === featurePillars.length - 1;
                      return (
                        <li key={pillar.title} className="relative">
                          <div className="mb-2.5 flex items-center gap-3">
                            <span
                              className="relative z-10 h-2 w-2 flex-shrink-0 rounded-full bg-green-600 ring-2 ring-green-500/20"
                              aria-hidden
                            />
                            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-black/45">
                              {pillar.title}
                            </p>
                          </div>

                          <div className="flex gap-3">
                            <div className="flex w-2 flex-shrink-0 justify-center" aria-hidden>
                              {!isLast && (
                                <span className="w-px flex-1 bg-gradient-to-b from-green-600/35 via-green-600/18 to-green-600/8" />
                              )}
                            </div>
                            <ul
                              className={
                                isLast
                                  ? "min-w-0 flex-1 space-y-1.5 pb-0 md:grid md:grid-cols-3 md:gap-x-3 md:gap-y-1 md:space-y-0"
                                  : "min-w-0 flex-1 space-y-1.5 pb-6 md:grid md:grid-cols-3 md:gap-x-3 md:gap-y-1 md:space-y-0"
                              }
                            >
                              {pillar.features.map((feature, i) => (
                                <motion.li
                                  key={feature.label}
                                  className="group flex items-center gap-2.5 rounded-xl border border-transparent py-1.5 transition-[background-color,border-color] duration-200 ease-out hover:border-green-500/[0.08] hover:bg-yellow-100 md:gap-2 md:px-1.5"
                                  initial={{ opacity: 0, x: -8 }}
                                  whileInView={{ opacity: 1, x: 0 }}
                                  viewport={{ once: true }}
                                  transition={{
                                    delay: 0.12 + pillarIndex * 0.08 + i * 0.03,
                                  }}
                                >
                                  <span
                                    className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-green-500/10 text-green-600"
                                    aria-hidden
                                  >
                                    <FontAwesomeIcon
                                      icon={feature.icon}
                                      className="h-3.5 w-3.5"
                                    />
                                  </span>
                                  <span className="whitespace-nowrap text-sm text-black/75">
                                    {feature.label}
                                  </span>
                                </motion.li>
                              ))}
                            </ul>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
